import { HOT } from "@hot-wallet/sdk";
import Big from "big.js";
import { t } from "i18next";
import { useCallback, useEffect, useState } from "react";
import { Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { mutate } from "swr";

import ArrowUpRight from "@/assets/icons/arrow-up-right.svg?react";
import Chevron from "@/assets/icons/chevron.svg?react";
import Check from "@/assets/icons/success-mark.svg?react";
import Warning from "@/assets/icons/warning.svg?react";
import BazookaLogo from "@/assets/images/bazooka-logo.png";
import totalTrustScore from "@/assets/images/total-trust-score.png";
import { Button } from "@/components/Button";
import { SlideDown } from "@/components/SlideDown/index.tsx";
import { ToastService } from "@/components/ToastService/index.tsx";
import { TOAST_ID } from "@/constants/index.ts";
import { ColoredNetworkIconMap } from "@/constants/map.tsx";
import { UploadType, useStore } from "@/hooks/store/useStore.ts";
import useCountdown from "@/hooks/useCountdown.ts";
import { API_ROUTES } from "@/interfaces/api/index.ts";
import { useModalContext } from "@/providers/modal-provider/context.ts";
import { useWalletSelector } from "@/providers/near-provider/index.tsx";
import { APP_ROUTES } from "@/routes/constants";
import { hapiApi } from "@/services/api/hapi.ts";
import { config } from "@/services/config/index.tsx";
import { NearTotalScoreContract } from "@/services/contracts/near/TotalScore.ts";
import near from "@/services/helpers/near.ts";
import { formatMonetaryAmount, formatTokenAmount } from "@/utils/amount.ts";
import { convertMillisecondsToDateParts } from "@/utils/date.ts";
import { delay } from "@/utils/index.ts";

import styles from "./styles.ts";

export type ApiSourceType = "Onchain" | "Offchain";
export const UPDATE_INTERVAL = 1000 * 7;
const preActiveTime = 10 * 60 * 1000;

export enum TotalScoreStage {
  PLANNED,
  PREACTIVE,
  ACTIVE,
  ENDED,
}

const calculateStage = (currentProgress?: number | string, maxProgress?: number | string) => {
  return TotalScoreStage.ENDED;

  if (config.trustScoreStartDate > Date.now() && config.trustScoreStartDate - Date.now() <= preActiveTime)
    return TotalScoreStage.PREACTIVE;

  if (Date.now() < config.trustScoreStartDate) return TotalScoreStage.PLANNED;
  if (currentProgress === undefined || !maxProgress) return TotalScoreStage.PLANNED;

  if (Number(currentProgress) <= Number(maxProgress)) return TotalScoreStage.ACTIVE;

  return TotalScoreStage.ENDED;
};

export const TotalScore: React.FC = () => {
  const navigate = useNavigate();
  const { accountId } = useWalletSelector();
  const [stage, setStage] = useState(calculateStage());
  const [metadata, setMetadata] = useState<string[] | undefined>();
  const modal = useModalContext();
  const selector = useWalletSelector();
  const [totalScore, setTotalScore] = useState<
    { method: ApiSourceType; totalScore: number; signature?: string; error: boolean } | { error: true } | undefined
  >(undefined);
  const [isOpen, setIsOpen] = useState(false);
  const countdown = useCountdown(config.trustScoreStartDate);

  const { networks, updateUploadType, user } = useStore((store) => ({
    networks: Object.values(store.user.networksData),
    updateUploadType: store.updateUploadType,
    user: store.user,
  }));

  const loadTrustScore = useCallback(async () => {
    if (accountId) {
      try {
        const hotConnection = await HOT.connection;
        if (!hotConnection?.telegramId) return;
        const data = await NearTotalScoreContract.get_mint_data();
        setMetadata(data);
        setStage(calculateStage(data?.[2], data?.[3]));
        updateUploadType(UploadType.Offchain);

        const onchainScore = await NearTotalScoreContract.get_user(hotConnection?.telegramId.toString());

        if (!onchainScore) {
          const offchainScore = await hapiApi.getTotalScore({ tgId: hotConnection?.telegramId, accountId: accountId });
          if (offchainScore.data.errorCode === 0) {
            setTotalScore({
              method: "Offchain",
              totalScore: Number(offchainScore.data.total_score),
              signature: offchainScore.data.signature,
              error: false,
            });
          }
        } else {
          setTotalScore({ method: "Onchain", totalScore: Number(onchainScore.score), error: false });
        }
      } catch (e) {
        setTotalScore({ error: true });

        console.log("error", e);
      }
    }
  }, [accountId, updateUploadType]);

  useEffect(() => {
    if (totalScore?.error || !accountId) return;
    if (!totalScore) {
      loadTrustScore();
    }
  }, [totalScore, accountId, loadTrustScore]);

  useEffect(() => {
    if ((stage === TotalScoreStage.PREACTIVE || stage === TotalScoreStage.ACTIVE) && accountId) {
      const intervalId = setInterval(async () => {
        try {
          const data = await NearTotalScoreContract.get_mint_data();
          if (!data) throw Error("Oops");
          setMetadata(data);
          setStage(calculateStage(data?.[2], data?.[3]));
        } catch (error) {
          console.error("Error, while updating counter for total score \n\n", error);
        }
      }, UPDATE_INTERVAL);
      return () => clearInterval(intervalId);
    }
  }, [accountId, stage]);

  const claim = async () => {
    const hotConnection = await HOT.connection;
    if (!hotConnection?.telegramId) return;
    if (!accountId || totalScore?.error || !totalScore || !totalScore.signature) return;

    const result = await near.mintTotalScore({
      selector: selector.selector,
      modal,
      accountId,
      signature: totalScore.signature,
      totalScore: totalScore.totalScore,
      tgId: hotConnection.telegramId,
    });

    if (result) {
      setTotalScore({ method: "Onchain", totalScore: Number(totalScore.totalScore), error: false });

      await delay(2000);
      await loadTrustScore();
      ToastService.clear(TOAST_ID.TRUST_SCORE);
    }
  };
  return (
    <styles.Container>
      <Button
        name="Back"
        iconLeft={<ArrowUpRight />}
        type="black"
        handler={() => {
          navigate(APP_ROUTES.HOME);

          if (!user.totalScore) {
            mutate(`${API_ROUTES.USER}/${accountId}`);
          }
        }}
      />
      <styles.TotalScoreContainer>
        <img src={totalTrustScore} />
        <styles.Title>
          {stage === TotalScoreStage.ENDED ? t("TotalScore.TrustScoreDropOver") : t("TotalScore.Title")}
        </styles.Title>
        <styles.SubTitle>
          {stage === TotalScoreStage.ENDED ? (
            t("TotalScore.TrustScoreDropOverSubtitle")
          ) : (
            <Trans i18nKey="TotalScore.SubTitle" components={{ br: <br /> }} />
          )}
        </styles.SubTitle>
        {stage === TotalScoreStage.ENDED ? (
          <Button name="FollowOnX" type="black" handler={() => window.open("https://x.com/i_am_hapi_one")} />
        ) : (
          <styles.LearnMore onClick={() => window.open("https://telegram.me/hapiHF/535478")}>
            {t("TotalScore.LearnMore")}
          </styles.LearnMore>
        )}
        {stage === TotalScoreStage.PLANNED && !config.trustScoreStartDate && (
          <styles.ComingSoon>{t("TotalScore.ComingSoon")}</styles.ComingSoon>
        )}

        {(stage === TotalScoreStage.PLANNED || stage === TotalScoreStage.PREACTIVE) && config.trustScoreStartDate && (
          <styles.ComingSoon>
            {countdown.days}d <span>|</span> {countdown.hours}
            <span> : </span>
            {countdown.minutes}
            <span> : </span>
            {countdown.seconds}
          </styles.ComingSoon>
        )}

        {stage === TotalScoreStage.ACTIVE && (
          <>
            {metadata && (
              <styles.ClaimedContainer>
                <div>
                  <p>{t("TotalScore.TokensClaimed")}</p>
                  <p>{`${formatMonetaryAmount(metadata[2])}/${formatMonetaryAmount(metadata[3])}`}</p>
                </div>
                <styles.Progress max={metadata[3]} value={Big(metadata[2]).eq("0") ? undefined : metadata[2]} />
              </styles.ClaimedContainer>
            )}

            {!totalScore?.error &&
              totalScore?.totalScore &&
              totalScore?.totalScore > 0 &&
              totalScore.method === "Offchain" && (
                <>
                  <styles.YourTrustScore>{t("TotalScore.YourTrustScore")}</styles.YourTrustScore>
                  <styles.TotalTrustScoreValue>{totalScore.totalScore}</styles.TotalTrustScoreValue>
                  <styles.Scores>
                    <styles.Networks>
                      <styles.Header
                        onClick={async () => {
                          console.log(user);
                          if (!user.totalScore) {
                            await updateUploadType(UploadType.Onchain);

                            await mutate(`${API_ROUTES.USER}/${accountId}`);
                          }
                          setIsOpen(!isOpen);
                        }}
                        $isOpen={isOpen}
                      >
                        <p>{t("TotalScore.TrustScoreBreakdown")}</p>
                        <styles.ScoreAmount>
                          <span>{totalScore?.totalScore}</span>
                          <span>/{networks?.length * 100}</span>
                        </styles.ScoreAmount>
                        <Chevron />
                      </styles.Header>
                      <SlideDown show={isOpen}>
                        <styles.Body>
                          {networks.map(({ network, score }) => {
                            const date = score?.expirationDate
                              ? convertMillisecondsToDateParts(score?.expirationDate)
                              : null;

                            return (
                              <styles.Network key={network}>
                                {ColoredNetworkIconMap[network]}
                                <styles.Text>
                                  <p>{network}</p>
                                  {date && (
                                    <p>
                                      <Trans
                                        i18nKey="Dashboard.ExpiresDate"
                                        components={{ bold: <strong /> }}
                                        values={{ day: date.day, month: date.month, year: date.year }}
                                      />
                                    </p>
                                  )}
                                </styles.Text>
                                <styles.ScoreAmount>
                                  <span>{score?.value || "-"}</span>
                                  <span>/100</span>
                                </styles.ScoreAmount>
                              </styles.Network>
                            );
                          })}
                        </styles.Body>
                      </SlideDown>
                    </styles.Networks>
                  </styles.Scores>
                  <Button
                    name="ClaimTotalScore"
                    additionalDataForName={{ amount: formatTokenAmount(metadata?.[0] || 0), symbol: "HAPI" }}
                    handler={claim}
                  />
                </>
              )}
          </>
        )}

        {!totalScore?.error && totalScore?.totalScore !== undefined && totalScore?.totalScore === 0 && (
          <styles.NoTrustScoreContainer>
            <Warning />
            <styles.NoTrustScoreTitle>{t("TotalScore.NoScoreTitle")}</styles.NoTrustScoreTitle>
            <styles.NoTrustScoreSubtitle>{t("TotalScore.NoScoreSubtitle")}</styles.NoTrustScoreSubtitle>
          </styles.NoTrustScoreContainer>
        )}

        {!totalScore?.error &&
          totalScore?.totalScore !== undefined &&
          totalScore?.totalScore > 0 &&
          totalScore.method === "Onchain" && (
            <>
              <styles.SuccessBox>
                <span>{t("TotalScore.ClaimedSuccess")}</span>
                <styles.CheckContainer>
                  <Check />
                </styles.CheckContainer>
              </styles.SuccessBox>
              <styles.ClaimedPoints>{t("TotalScore.ClaimedTitle")}</styles.ClaimedPoints>
              <styles.ClaimedValue>{totalScore.totalScore}</styles.ClaimedValue>
              <styles.ClaimedDescription>{t("TotalScore.ClaimedDescription")}</styles.ClaimedDescription>
              <styles.BazookaLogo src={BazookaLogo} />
            </>
          )}
      </styles.TotalScoreContainer>
    </styles.Container>
  );
};
